import React, { useEffect } from 'react';
import { motion, useSpring } from 'framer-motion';
import { useCountUp } from '@/hooks/useCountUp';
import { Spinner } from '@/components/ui/spinner';
import { ease } from '@/lib/motion';
import { useTranslation } from 'react-i18next';

/**
 * Eine Farbe je Score-Band, keine Ampel-Verschiebung mehr: die Bandgrenzen
 * 40 und 70 bleiben unveraendert, die Farben entsprechen denen der
 * Score-Badge (badge.tsx scoreCritical/scoreWarning/scoreSolid,
 * ProductStatusPill.tsx, GRADE_STYLES in FeedCheckHeader.tsx). Vorher
 * lieferte `getGradientColors` einen Verlauf VON der Bandfarbe ZUR
 * naechsten Bandfarbe (`colors.end` war also stets eine Farbe zu weit
 * oben); ein Vollton-Ring darf das nicht mehr tun, sonst rutscht ein Feed
 * mit Punktzahl 20 in Bernstein statt Rot.
 */
function getRingColor(score: number) {
  if (score < 40) return 'hsl(var(--destructive))';
  if (score < 70) return 'hsl(var(--warning))';
  return 'hsl(var(--success))';
}

interface HeroDonutProps {
  score: number;
  isUpdating?: boolean;
  size?: number;
  /**
   * Die "von 100"-Unterzeile. Opt-out fuer kleine Ringe: sie skaliert mit
   * `size * 0.07` und waere bei 72 px rund 5 px gross - unlesbar ist
   * schlechter als weg. Alle bestehenden Aufrufer bleiben unveraendert.
   */
  subLabel?: boolean;
  /**
   * Die Note IM Ring, unter der Zahl (Entlastung Paket 3, Spec 5.3: "HeroDonut
   * 132 px mit Zahl und Note im Ring").
   *
   * Fertig formatiert vom Aufrufer ("Note A"), nicht der rohe Buchstabe: die
   * Uebersetzung liegt im `optimization`-Namensraum, und diese Komponente
   * bekaeme sonst eine zweite Copy-Quelle fuer dieselbe Aussage.
   *
   * Gesetzt schaltet `subLabel` aus, auch wenn der Aufrufer ihn nicht selbst
   * abwaehlt: drei Textzeilen in einem 132-px-Ring sind nicht mehr lesbar, und
   * "von 100" ist die verzichtbare der beiden -- die Note traegt die Aussage,
   * die Skala steht im Druck-Report.
   */
  grade?: string;
}

export const HeroDonut: React.FC<HeroDonutProps> = ({ score, isUpdating = false, size = 200, subLabel = true, grade }) => {
  const { t } = useTranslation('optimization');
  const displayScore = useCountUp(score, 1200);
  const strokeWidth = size * 0.1;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const ringColor = getRingColor(score);

  const springOffset = useSpring(circumference, { stiffness: 60, damping: 20 });

  useEffect(() => {
    const targetOffset = circumference - (score / 100) * circumference;
    springOffset.set(targetOffset);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [score, circumference]);

  const fontSize = Math.round(size * 0.24);
  const subFontSize = Math.round(size * 0.07);

  return (
    <motion.div
      data-testid="hero-donut"
      // WP5 K08: feste Box in Ringgroesse. Ohne eigene Breite nahm der Wrapper
      // die Breite seines Elternknopfs an, und das absolut zentrierte Label
      // sass mobil rechts NEBEN dem Ring.
      className="relative flex-shrink-0"
      style={{ width: size, height: size }}
      animate={{ opacity: isUpdating ? 0.5 : 1 }}
      transition={ease.smooth}
    >
      <svg width={size} height={size} className="block -rotate-90">
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" className="stroke-muted" strokeWidth={strokeWidth} />
        <motion.circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={ringColor}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          style={{ strokeDashoffset: springOffset }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-heading font-bold leading-none text-foreground" style={{ fontSize: `${fontSize}px` }}>
          {displayScore}
        </span>
        {grade ? (
          <span
            data-testid="hero-donut-grade"
            className="font-bold text-muted-foreground"
            style={{ fontSize: `${Math.round(size * 0.1)}px` }}
          >
            {grade}
          </span>
        ) : subLabel && (
          <span className="font-medium text-muted-foreground" style={{ fontSize: `${subFontSize}px` }}>{t('heroDonut.outOf')}</span>
        )}
      </div>
      {isUpdating && (
        <div className="absolute inset-0 flex items-center justify-center">
          <Spinner size="md" />
        </div>
      )}
    </motion.div>
  );
};
